import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
  OutlinedInput,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import RefreshIcon from "@material-ui/icons/Refresh";
import SearchIcon from "@material-ui/icons/Search";
import { useNotify } from "react-admin";
import { ENDPOINTS } from "../apiConstants";
import { getConfigGet, getConfigPatch } from "../getConfig";

type Provider = "truelayer" | "payit" | "fena_pisp";
type Toggle = "enabled" | "topup_enabled";

interface Bank {
  id: number | null;
  provider_id: string;
  display_name: string;
  logo_uri: string | null;
  provider: Provider;
  enabled: boolean | number;
  topup_enabled: boolean | number;
  updatedBy: string | null;
  updatedAt: string | null;
}

interface Stats {
  total: number;
  executed: number;
  failed: number;
  refunded: number;
  in_progress: number;
  success_rate: number | null;
  refund_rate: number | null;
  net_success_rate: number | null;
  executed_amount: number;
}

interface BankPerformance extends Stats {
  id: number | null;
  provider: Provider;
  provider_id: string;
  display_name: string;
  logo_uri: string | null;
  enabled: boolean | null;
  topup_enabled: boolean | null;
  last_payment_at: string | null;
  last_success_at: string | null;
}

interface PerformanceResponse {
  hours: number;
  providers: (Stats & { provider: Provider })[];
  banks: BankPerformance[];
}

const PROVIDER_LABELS: Record<Provider, string> = {
  truelayer: "TrueLayer",
  payit: "Payit",
  fena_pisp: "Fena PISP",
};
const PROVIDER_COLOURS: Record<Provider, string> = {
  truelayer: "#1e88e5",
  payit: "#8e24aa",
  fena_pisp: "#00897b",
};
const TOGGLE_LABELS: Record<Toggle, string> = {
  enabled: "Gift card",
  topup_enabled: "Top-up",
};
const WINDOWS = [1, 2, 4, 6];
const AUTO_REFRESH_MS = 60000;
const WARN_RATE = 85;
const ALERT_RATE = 70;
const REFUND_WARN_RATE = 10;
const REFUND_ALERT_RATE = 20;
const MIN_COMPLETED_PAYMENTS = 10;

const useStyles = makeStyles((theme) => ({
  container: { padding: theme.spacing(2), maxWidth: 1600, display: "grid", gridTemplateColumns: "minmax(0, 1fr)" },
  tabs: { marginBottom: theme.spacing(2), borderBottom: `1px solid ${theme.palette.divider}` },
  filters: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: `${theme.spacing(2)}px`,
    marginBottom: theme.spacing(2),
  },
  filter: { minWidth: 170 },
  spacer: { flex: 1 },
  chipGroup: { display: "flex", flexWrap: "wrap", gap: `${theme.spacing(1)}px` },
  switchLabel: { margin: 0 },
  switchText: { fontSize: "0.875rem", whiteSpace: "nowrap" },
  refreshGroup: { display: "flex", alignItems: "center", gap: `${theme.spacing(1.5)}px`, marginLeft: "auto" },
  summary: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: `${theme.spacing(2)}px`,
    marginBottom: theme.spacing(2),
  },
  summaryCard: { borderTop: "4px solid" },
  metric: { fontSize: "1.75rem", fontWeight: 600, lineHeight: 1.2 },
  muted: { color: theme.palette.text.secondary, fontSize: "0.8rem" },
  head: { backgroundColor: theme.palette.type === "dark" ? theme.palette.grey[800] : theme.palette.grey[100] },
  headCell: { fontWeight: 600, whiteSpace: "nowrap" },
  bankCell: { display: "flex", alignItems: "center", gap: `${theme.spacing(1)}px` },
  logo: { width: 24, height: 24, objectFit: "contain", borderRadius: 4 },
  logoPlaceholder: { width: 24, height: 24 },
  mono: { fontFamily: "monospace", fontSize: "0.8rem" },
  providerChip: { color: "#fff", fontWeight: 600 },
  warnRow: { backgroundColor: "rgba(255, 152, 0, 0.08)" },
  alertRow: { backgroundColor: "rgba(244, 67, 54, 0.1)" },
  center: { display: "flex", justifyContent: "center", padding: theme.spacing(4) },
}));

const formatDate = (value: string | null) => (value ? new Date(value).toLocaleString("en-GB") : "—");
const formatTime = (value: string | null) =>
  value ? new Date(value).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : "—";
const formatCurrency = (amount: number) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(amount || 0);
const rateColour = (rate: number | null) =>
  rate === null ? undefined : rate < ALERT_RATE ? "#e53935" : rate < WARN_RATE ? "#fb8c00" : "#43a047";
const refundColour = (rate: number | null) =>
  rate === null ? undefined : rate >= REFUND_ALERT_RATE ? "#e53935" : rate >= REFUND_WARN_RATE ? "#fb8c00" : undefined;
const formatRate = (rate: number | null) => (rate === null ? "—" : `${rate}%`);

const ProviderChip = ({ provider }: { provider: Provider }) => {
  const classes = useStyles();
  return (
    <Chip
      size="small"
      label={PROVIDER_LABELS[provider] || provider}
      className={classes.providerChip}
      style={{ backgroundColor: PROVIDER_COLOURS[provider] || "#757575" }}
    />
  );
};

const BankName = ({ name, logo }: { name: string; logo: string | null }) => {
  const classes = useStyles();
  return (
    <div className={classes.bankCell}>
      {logo ? <img src={logo} alt="" className={classes.logo} /> : <span className={classes.logoPlaceholder} />}
      <span>{name}</span>
    </div>
  );
};

const ProviderFilter = ({ value, onChange }: { value: string; onChange: (value: string) => void }) => {
  const classes = useStyles();
  return (
    <FormControl variant="outlined" size="small" className={classes.filter}>
      <InputLabel shrink>Provider</InputLabel>
      <Select value={value} onChange={(e) => onChange(e.target.value as string)} displayEmpty input={<OutlinedInput notched label="Provider" />}>
        <MenuItem value="">All providers</MenuItem>
        {(Object.keys(PROVIDER_LABELS) as Provider[]).map((p) => (
          <MenuItem key={p} value={p}>
            {PROVIDER_LABELS[p]}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

const BanksTab = () => {
  const classes = useStyles();
  const notify = useNotify();
  const [banks, setBanks] = useState<Bank[]>([]);
  const [loading, setLoading] = useState(false);
  const [provider, setProvider] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [pending, setPending] = useState<{ bank: Bank; field: Toggle; value: boolean } | null>(null);
  const [saving, setSaving] = useState(false);

  const fetchBanks = useCallback(() => {
    setLoading(true);
    axios
      .get(ENDPOINTS.PAYMENT_PROVIDER_BANKS, getConfigGet(provider ? { provider } : {}))
      .then((res: any) =>
        setBanks(
          (res?.data?.banks || [])
            .filter((bank: Bank) => bank.id !== null)
            .sort((a: Bank, b: Bank) => a.provider.localeCompare(b.provider) || a.display_name.localeCompare(b.display_name))
        )
      )
      .catch((error) => notify(error?.response?.data?.message || "Failed to fetch banks", "error"))
      .finally(() => setLoading(false));
  }, [provider, notify]);

  useEffect(() => {
    fetchBanks();
  }, [fetchBanks]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return banks.filter((bank) => {
      if (status === "enabled" && !bank.enabled) return false;
      if (status === "disabled" && bank.enabled) return false;
      if (status === "topup" && !bank.topup_enabled) return false;
      return !term || bank.display_name.toLowerCase().includes(term) || bank.provider_id.toLowerCase().includes(term);
    });
  }, [banks, status, search]);

  const confirmToggle = () => {
    if (!pending) return;
    const { bank, field, value } = pending;
    setSaving(true);
    axios
      .patch(`${ENDPOINTS.PAYMENT_PROVIDER_BANKS}/${bank.id}`, { [field]: value }, { headers: getConfigPatch() })
      .then((res: any) => {
        setBanks((rows) => rows.map((row) => (row.id === bank.id ? { ...row, ...res.data } : row)));
        notify(`${bank.display_name} ${TOGGLE_LABELS[field].toLowerCase()} ${value ? "enabled" : "disabled"}`, "info");
      })
      .catch((error) => notify(error?.response?.data?.message || "Failed to update bank", "error"))
      .finally(() => {
        setSaving(false);
        setPending(null);
      });
  };

  const counts = useMemo(
    () => ({
      total: banks.length,
      enabled: banks.filter((b) => b.enabled).length,
      topup: banks.filter((b) => b.topup_enabled).length,
    }),
    [banks]
  );

  return (
    <>
      <div className={classes.filters}>
        <ProviderFilter value={provider} onChange={setProvider} />
        <FormControl variant="outlined" size="small" className={classes.filter}>
          <InputLabel>Status</InputLabel>
          <Select value={status} onChange={(e) => setStatus(e.target.value as string)} label="Status">
            <MenuItem value="">All</MenuItem>
            <MenuItem value="enabled">Gift card enabled</MenuItem>
            <MenuItem value="disabled">Gift card disabled</MenuItem>
            <MenuItem value="topup">Top-up enabled</MenuItem>
          </Select>
        </FormControl>
        <TextField
          variant="outlined"
          size="small"
          placeholder="Search bank or provider id"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <div className={classes.spacer} />
        <Button variant="outlined" color="primary" startIcon={<RefreshIcon />} onClick={fetchBanks} disabled={loading}>
          Refresh
        </Button>
      </div>
      <Typography className={classes.muted} gutterBottom>
        {counts.total} banks · {counts.enabled} gift card enabled · {counts.topup} top-up enabled
      </Typography>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead className={classes.head}>
            <TableRow>
              {["ID", "Bank", "Provider id", "Provider", "Gift card", "Top-up", "Updated by", "Updated at"].map((h) => (
                <TableCell key={h} className={classes.headCell}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8}>
                  <div className={classes.center}>
                    <CircularProgress size={28} />
                  </div>
                </TableCell>
              </TableRow>
            ) : visible.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No banks match these filters
                </TableCell>
              </TableRow>
            ) : (
              visible.map((bank) => (
                <TableRow key={bank.id} hover>
                  <TableCell>{bank.id}</TableCell>
                  <TableCell>
                    <BankName name={bank.display_name} logo={bank.logo_uri} />
                  </TableCell>
                  <TableCell className={classes.mono}>{bank.provider_id}</TableCell>
                  <TableCell>
                    <ProviderChip provider={bank.provider} />
                  </TableCell>
                  {(["enabled", "topup_enabled"] as Toggle[]).map((field) => (
                    <TableCell key={field}>
                      <FormControlLabel
                        control={
                          <Switch
                            color="primary"
                            checked={Boolean(bank[field])}
                            onChange={(e) => setPending({ bank, field, value: e.target.checked })}
                          />
                        }
                        label={bank[field] ? "On" : "Off"}
                      />
                    </TableCell>
                  ))}
                  <TableCell>{bank.updatedBy || "—"}</TableCell>
                  <TableCell>{formatDate(bank.updatedAt)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={Boolean(pending)} onClose={() => !saving && setPending(null)}>
        <DialogTitle>
          {pending?.value ? "Enable" : "Disable"} {pending && TOGGLE_LABELS[pending.field].toLowerCase()}?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pending &&
              `${pending.bank.display_name} (${PROVIDER_LABELS[pending.bank.provider]}) will ${
                pending.value ? "appear in" : "disappear from"
              } the ${TOGGLE_LABELS[pending.field].toLowerCase()} bank list for users straight away.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPending(null)} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={confirmToggle}
            color={pending?.value ? "primary" : "secondary"}
            variant="contained"
            disabled={saving}
          >
            {saving ? <CircularProgress size={20} /> : pending?.value ? "Enable" : "Disable"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const PerformanceTab = () => {
  const classes = useStyles();
  const notify = useNotify();
  const [hours, setHours] = useState(2);
  const [provider, setProvider] = useState("");
  const [data, setData] = useState<PerformanceResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [hideIdle, setHideIdle] = useState(true);
  const [fetchedAt, setFetchedAt] = useState<Date | null>(null);

  const fetchPerformance = useCallback(() => {
    setLoading(true);
    axios
      .get(ENDPOINTS.PAYMENT_PROVIDER_BANKS, getConfigGet({ hours, ...(provider ? { provider } : {}) }))
      .then((res: any) => {
        setData(res?.data || null);
        setFetchedAt(new Date());
      })
      .catch((error) => notify(error?.response?.data?.message || "Failed to fetch performance", "error"))
      .finally(() => setLoading(false));
  }, [hours, provider, notify]);

  useEffect(() => {
    fetchPerformance();
    if (!autoRefresh) return undefined;
    const timer = setInterval(fetchPerformance, AUTO_REFRESH_MS);
    return () => clearInterval(timer);
  }, [fetchPerformance, autoRefresh]);

  const rows = (data?.banks || []).filter((bank) => !hideIdle || bank.total > 0);
  const summaries = (data?.providers || []).filter((p) => p.total > 0 || p.provider === provider);

  return (
    <>
      <div className={classes.filters}>
        <div className={classes.chipGroup}>
          {WINDOWS.map((w) => (
            <Chip
              key={w}
              label={`Last ${w} hr`}
              clickable
              color={hours === w ? "primary" : "default"}
              variant={hours === w ? "default" : "outlined"}
              onClick={() => setHours(w)}
            />
          ))}
        </div>
        <ProviderFilter value={provider} onChange={setProvider} />
        <FormControlLabel
          classes={{ root: classes.switchLabel, label: classes.switchText }}
          control={<Switch size="small" checked={hideIdle} onChange={(e) => setHideIdle(e.target.checked)} color="primary" />}
          label="Hide banks with no payments"
        />
        <FormControlLabel
          classes={{ root: classes.switchLabel, label: classes.switchText }}
          control={<Switch size="small" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} color="primary" />}
          label="Auto-refresh (1 min)"
        />
        <div className={classes.refreshGroup}>
          <Typography className={classes.muted}>
            {fetchedAt ? `Updated ${fetchedAt.toLocaleTimeString("en-GB")}` : ""}
          </Typography>
          <Button variant="outlined" color="primary" startIcon={<RefreshIcon />} onClick={fetchPerformance} disabled={loading}>
            Refresh
          </Button>
        </div>
      </div>

      {!data && loading ? (
        <div className={classes.center}>
          <CircularProgress />
        </div>
      ) : (
        <>
          <div className={classes.summary}>
            {summaries.length === 0 && (
              <Typography className={classes.muted}>No bank payments in the last {hours} hr.</Typography>
            )}
            {summaries.map((p) => (
              <Card key={p.provider} className={classes.summaryCard} style={{ borderTopColor: PROVIDER_COLOURS[p.provider] }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <ProviderChip provider={p.provider} />
                    <Typography className={classes.muted}>last {data?.hours} hr</Typography>
                  </Box>
                  <Typography className={classes.metric} style={{ color: rateColour(p.net_success_rate) }}>
                    {formatRate(p.net_success_rate)}
                  </Typography>
                  <Typography className={classes.muted}>
                    net success · payment success {formatRate(p.success_rate)} ·{" "}
                    <span style={{ color: refundColour(p.refund_rate) }}>refunded {formatRate(p.refund_rate)}</span>
                  </Typography>
                  <Box display="flex" justifyContent="space-between" mt={1}>
                    <span>{p.total} payments</span>
                    <span style={{ color: "#43a047" }}>{p.executed} ok</span>
                    <span style={{ color: "#e53935" }}>{p.failed} failed</span>
                    <span style={{ color: "#fb8c00" }}>{p.refunded} refunded</span>
                    <span>{p.in_progress} pending</span>
                  </Box>
                  <Typography className={classes.muted} style={{ marginTop: 4 }}>
                    {formatCurrency(p.executed_amount)} executed
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </div>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead className={classes.head}>
                <TableRow>
                  {["Bank", "Provider", "Gift card", "Payments", "Executed", "Failed", "Pending", "Payment success", "Refunded", "Refund rate", "Net success", "Executed £", "Last payment", "Last success"].map(
                    (h) => (
                      <TableCell key={h} className={classes.headCell}>
                        {h}
                      </TableCell>
                    )
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={14} align="center">
                      No payments in this window
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((bank) => {
                    const rate = bank.net_success_rate;
                    const flagged = bank.executed + bank.failed >= MIN_COMPLETED_PAYMENTS;
                    const rowClass = !flagged || rate === null
                      ? undefined
                      : rate < ALERT_RATE ? classes.alertRow : rate < WARN_RATE ? classes.warnRow : undefined;
                    return (
                      <TableRow key={`${bank.provider}:${bank.provider_id}`} className={rowClass} hover>
                        <TableCell>
                          <BankName name={bank.display_name} logo={bank.logo_uri} />
                          <div className={`${classes.mono} ${classes.muted}`}>{bank.provider_id}</div>
                        </TableCell>
                        <TableCell>
                          <ProviderChip provider={bank.provider} />
                        </TableCell>
                        <TableCell>
                          {bank.enabled === null ? (
                            <Chip size="small" label="Not in table" />
                          ) : (
                            <Chip
                              size="small"
                              label={bank.enabled ? "On" : "Off"}
                              color={bank.enabled ? "primary" : "default"}
                              variant={bank.enabled ? "default" : "outlined"}
                            />
                          )}
                        </TableCell>
                        <TableCell>{bank.total}</TableCell>
                        <TableCell style={{ color: "#43a047" }}>{bank.executed}</TableCell>
                        <TableCell style={{ color: bank.failed ? "#e53935" : undefined }}>{bank.failed}</TableCell>
                        <TableCell>{bank.in_progress}</TableCell>
                        <TableCell style={{ color: flagged ? rateColour(bank.success_rate) : undefined }}>{formatRate(bank.success_rate)}</TableCell>
                        <TableCell>{bank.refunded}</TableCell>
                        <TableCell style={{ color: flagged ? refundColour(bank.refund_rate) : undefined }}>{formatRate(bank.refund_rate)}</TableCell>
                        <TableCell style={{ color: flagged ? rateColour(rate) : undefined, fontWeight: 600 }}>{formatRate(rate)}</TableCell>
                        <TableCell>{formatCurrency(bank.executed_amount)}</TableCell>
                        <TableCell>{formatTime(bank.last_payment_at)}</TableCell>
                        <TableCell>{formatTime(bank.last_success_at)}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography className={classes.muted} style={{ marginTop: 8 }}>
            Gift card payments only; pending payments are left out. Payment success = executed ÷ (executed + failed).
            Refund rate = executed but refunded or Sprive-cancelled ÷ executed. Net success = (executed − refunded) ÷
            (executed + failed). Rows are amber under {WARN_RATE}% and red under {ALERT_RATE}% net success, once a bank has
            {MIN_COMPLETED_PAYMENTS}+ completed (executed or failed) payments.
          </Typography>
        </>
      )}
    </>
  );
};

const PaymentProviderDashboard = () => {
  const classes = useStyles();
  const [tab, setTab] = useState(0);

  return (
    <div className={classes.container}>
      <Typography variant="h5" gutterBottom>
        Payment Providers
      </Typography>
      <Tabs value={tab} onChange={(_, value) => setTab(value)} indicatorColor="primary" textColor="primary" className={classes.tabs}>
        <Tab label="Banks" />
        <Tab label="Performance" />
      </Tabs>
      {tab === 0 ? <BanksTab /> : <PerformanceTab />}
    </div>
  );
};

export default PaymentProviderDashboard;
